import type { SupabaseClient } from '@supabase/supabase-js';
import { awaitJob } from '../../lib/await-job.js';
import { openModal } from '@vario-connect/frontend-design';

interface DialogCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  unit: {
    storefront: string;
    idUnit: string;
    ean: string | null;
    /** Kaufland-SKU (`id_offer`). Fallback fuer die Suchvorbelegung, wenn keine EAN vorliegt. */
    sku?: string | null;
    title: string | null;
    /** Wenn gesetzt: Unit ist schon einem Vario-Article zugewiesen. Aktiviert die Aufheben-Aktion. */
    varioArticleId?: string | null;
    varioArticleNumber?: string | null;
  };
  /** Called after the assignment job completes successfully so the grid can reload that row. */
  onDone: () => void;
}

interface VarioArticleMatch {
  id: string;
  number: string | null;
  description: string | null;
  ean: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;

export function openArticleDialog(ctx: DialogCtx): void {
  const isMapped = !!ctx.unit.varioArticleId;
  const mappedNumber = ctx.unit.varioArticleNumber ?? ctx.unit.varioArticleId ?? '—';
  const titleLabel = ctx.unit.title ?? '(ohne Titel)';

  // Angezeigt wird die SKU (`id_offer`) wie in der Grid-Spalte. Frueher stand hier `idUnit`, also
  // die Kaufland-Unit-Id — unter der Beschriftung "SKU" irrefuehrend. `idUnit` bleibt der Fallback,
  // falls eine Zeile keine SKU traegt.
  const skuLabel = ctx.unit.sku ?? ctx.unit.idUnit;
  // Im nicht zugeordneten Zweig sind SKU und EAN anklickbar: der Wert wandert ins Suchfeld und die
  // Suche startet sofort. Deckt den Fall ab, dass die EAN-Vorbelegung nichts findet und man auf die
  // SKU umschwenken will, ohne abzutippen. Im zugeordneten Zweig gibt es kein Suchfeld → nur Text.
  const term = (label: string, value: string) =>
    `<button type="button" class="kl-meta-term" data-term="${escapeHtml(value)}" title="Nach dieser ${label} suchen">${escapeHtml(value)}</button>`;

  const meta = `
    <div class="kl-modal-meta">
      <strong>SKU ${isMapped ? escapeHtml(skuLabel) : term('SKU', skuLabel)}</strong>
      <div class="text-grey">${escapeHtml(titleLabel)}${ctx.unit.ean ? ` · EAN ${isMapped ? escapeHtml(ctx.unit.ean) : term('EAN', ctx.unit.ean)}` : ''}</div>
    </div>
  `;

  const body = isMapped
    ? `
      ${meta}
      <div class="kl-mapped-card">
        <div class="kl-mapped-card-icon"><span class="v-icon fa-light fa-link"></span></div>
        <div class="kl-mapped-card-body">
          <div class="kl-mapped-card-title">Aktuell zugeordnet</div>
          <div class="kl-mapped-card-num text-mono">${escapeHtml(mappedNumber)}</div>
        </div>
        <button type="button" class="v-button kl-mapped-unassign" data-action="unassign">
          <span class="v-icon fa-light fa-xmark"></span>
          Zuordnung aufheben
        </button>
      </div>
      <div class="v-banner v-banner-warning">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>Beim Aufheben wird der Verkaufskanal <strong>Kaufland ${escapeHtml(ctx.unit.storefront.toUpperCase())}</strong> in Vario am Artikel <strong>entfernt</strong>. Bestände werden nicht mehr gesynct. Der Artikel selbst bleibt bestehen.</div>
      </div>
      <div class="kl-modal-error" id="kl-art-error" hidden></div>
    `
    : `
      ${meta}
      <div class="kl-form-row">
        <label>Vario-Artikel suchen</label>
        <div class="v-input kl-search-input">
          <span class="v-icon fa-light fa-magnifying-glass"></span>
          <input id="kl-art-search" type="search" placeholder="EAN, SKU oder Bezeichnung …" autocomplete="off" />
          <button type="button" id="kl-art-search-clear" class="kl-search-clear" aria-label="Suche zurücksetzen" hidden>
            <span class="v-icon fa-light fa-xmark"></span>
          </button>
        </div>
        <div class="kl-form-hint">Vorbelegt mit der EAN dieser Position, sonst mit der SKU. Mindestens 3 Zeichen.</div>
      </div>
      <div class="kl-search-results" id="kl-art-results"></div>
      <div class="kl-modal-error" id="kl-art-error" hidden></div>
    `;

  const modal = openModal({
    title: isMapped ? 'Vario-Artikel-Zuordnung' : 'Vario-Artikel zuordnen',
    icon: 'fa-link',
    width: 640,
    body,
    // Nur Zuordnen, kein Anlegen: Vario-Artikel entstehen ausschliesslich im ERP, die App
    // verknuepft sie lediglich. Frueher stand hier zusaetzlich „Artikel in Vario anlegen".
    footer: `<button type="button" class="v-button" data-modal-cancel>Schließen</button>`,
  });

  const errorEl = modal.el.querySelector<HTMLDivElement>('#kl-art-error')!;

  const showError = (msg: string) => {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  };
  const clearError = () => {
    errorEl.textContent = '';
    errorEl.hidden = true;
  };

  if (isMapped) {
    // Zweistufiger Klick statt Sofort-Ausfuehrung: Das Aufheben entfernt den Verkaufskanal in
    // Vario und laesst sich nicht per Knopfdruck zurueckholen. `window.confirm` ist projektweit
    // verboten, deshalb schaerft der erste Klick den Button und der zweite fuehrt aus; nach 4 s
    // ohne zweiten Klick faellt er in den Ausgangszustand zurueck. Gleiche Mechanik wie in Otto.
    const unassignBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="unassign"]')!;
    const unassignHtml = unassignBtn.innerHTML;
    // Folgen-Hinweis im Fenster statt im Browser-Dialog. Er erscheint erst im geschaerften
    // Zustand, damit die Karte im Ruhezustand nicht mit einer Warnung zugestellt ist.
    const armHint = document.createElement('p');
    armHint.className = 'kl-unassign-hint';
    armHint.hidden = true;
    armHint.textContent =
      `Das Listing am Verkaufskanal „Kaufland ${ctx.unit.storefront.toUpperCase()}" wird in Vario `
      + 'deaktiviert. Der Artikel selbst und alle anderen Storefronts bleiben unverändert.';
    unassignBtn.insertAdjacentElement('afterend', armHint);

    let armed = false;
    let armTimer: number | null = null;
    const disarm = () => {
      armed = false;
      unassignBtn.classList.remove('is-armed');
      unassignBtn.innerHTML = unassignHtml;
      armHint.hidden = true;
    };
    unassignBtn.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        unassignBtn.classList.add('is-armed');
        unassignBtn.innerHTML = '<span class="v-icon fa-light fa-triangle-exclamation"></span> Wirklich aufheben? Nochmal klicken';
        armHint.hidden = false;
        if (armTimer !== null) window.clearTimeout(armTimer);
        armTimer = window.setTimeout(disarm, 6000) as unknown as number;
        return;
      }
      if (armTimer !== null) window.clearTimeout(armTimer);
      unassignBtn.classList.remove('is-armed');
      armHint.hidden = true;
      void unassign(unassignBtn);
    });
  } else {
    const searchInput = modal.el.querySelector<HTMLInputElement>('#kl-art-search')!;
    const clearBtn = modal.el.querySelector<HTMLButtonElement>('#kl-art-search-clear')!;
    const resultsEl = modal.el.querySelector<HTMLDivElement>('#kl-art-results')!;

    const updateClearVisibility = () => { clearBtn.hidden = searchInput.value.length === 0; };

    let debounceTimer: number | null = null;
    searchInput.addEventListener('input', () => {
      updateClearVisibility();
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => void runSearch(searchInput.value, resultsEl), SEARCH_DEBOUNCE_MS) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      searchInput.value = '';
      updateClearVisibility();
      resultsEl.innerHTML = renderEmptyHint();
      searchInput.focus();
    });

    // SKU/EAN im Kopf: Klick uebernimmt den Wert und sucht sofort.
    modal.el.querySelectorAll<HTMLButtonElement>('[data-term]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset['term'] ?? '';
        if (!value) return;
        searchInput.value = value;
        updateClearVisibility();
        searchInput.focus();
        void runSearch(value, resultsEl);
      });
    });

    // Vorbelegung: EAN, sonst die SKU. Ohne den SKU-Fallback startete jede Position OHNE EAN mit
    // leerem Feld, obwohl die Suche Artikelnummern per `number LIKE` durchaus findet — der Nutzer
    // musste die SKU von Hand abtippen. Analog zum Otto-Dialog.
    const prefill = ctx.unit.ean ?? ctx.unit.sku ?? null;
    if (prefill) {
      searchInput.value = prefill;
      updateClearVisibility();
      void runSearch(prefill, resultsEl);
    } else {
      resultsEl.innerHTML = renderEmptyHint();
    }
    setTimeout(() => { searchInput.focus(); searchInput.select(); }, 50);
  }


  async function runSearch(term: string, resultsEl: HTMLDivElement) {
    const trimmed = term.trim();
    if (trimmed.length === 0) {
      resultsEl.innerHTML = renderEmptyHint();
      return;
    }
    if (trimmed.length < 3) {
      resultsEl.innerHTML = `
        <div class="kl-search-empty">
          <span class="v-icon fa-light fa-circle-info"></span>
          Mindestens 3 Zeichen eingeben.
        </div>
      `;
      return;
    }
    resultsEl.innerHTML = `
      <div class="kl-search-loading">
        <span class="v-icon fa-regular fa-spinner-third fa-spin"></span>
        Suche in Vario …
      </div>
    `;
    clearError();
    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-search-articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ search: trimmed, limit: 20 }),
      });
      const body = (await res.json()) as { articles?: VarioArticleMatch[]; error?: { message: string } };
      if (!res.ok || !body.articles) {
        throw new Error(body.error?.message ?? `HTTP ${res.status}`);
      }
      renderResults(body.articles, resultsEl);
    } catch (e) {
      showError(`Suche fehlgeschlagen: ${(e as Error).message}`);
      resultsEl.innerHTML = '';
    }
  }

  function renderResults(articles: VarioArticleMatch[], resultsEl: HTMLDivElement): void {
    if (articles.length === 0) {
      resultsEl.innerHTML = `
        <div class="v-banner v-banner-info">
          <span class="v-icon fa-light fa-circle-info"></span>
          <div>
            <strong>Keine Treffer.</strong> Der passende Artikel muss zuerst in Vario
            angelegt werden, danach lässt er sich hier zuordnen.
          </div>
        </div>
      `;
      return;
    }
    resultsEl.innerHTML = articles.map((a) => `
      <button type="button" class="kl-search-result" data-action="assign" data-id="${escapeHtml(a.id)}" data-number="${escapeHtml(a.number ?? '')}">
        <div class="kl-search-result-main">
          <div class="kl-search-result-title">${escapeHtml(a.description ?? '(ohne Bezeichnung)')}</div>
          <div class="kl-search-result-meta">
            <span class="text-mono">${escapeHtml(a.number ?? '—')}</span>
            <span class="text-grey">· EAN <span class="text-mono">${escapeHtml(a.ean ?? '—')}</span></span>
          </div>
        </div>
        <span class="v-icon fa-light fa-arrow-right kl-search-result-arrow"></span>
      </button>
    `).join('');
    resultsEl.querySelectorAll<HTMLButtonElement>('button[data-action="assign"]').forEach((btn) => {
      btn.addEventListener('click', () => void assign(btn.dataset['id']!, btn.dataset['number'] || null, btn));
    });
  }

  function renderEmptyHint(): string {
    return `
      <div class="kl-search-empty">
        <span class="v-icon fa-light fa-magnifying-glass"></span>
        Suchbegriff eingeben — EAN, SKU oder Bezeichnung.
      </div>
    `;
  }

  async function assign(varioArticleId: string, varioArticleNumber: string | null, btn: HTMLButtonElement): Promise<void> {
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.innerHTML = `
      <div class="kl-search-result-main">
        <div class="kl-search-result-title"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Zuweisen …</div>
      </div>
    `;
    clearError();
    try {
      // number ins draft mitschicken — der Worker schreibt sie ins
      // article_mappings.vario_article_number-Feld, das die UI in der Spalte
      // "Vario-SKU" anzeigt. Ohne diese Zeile bleibt die Spalte leer fuer
      // alle existing-article-Zuordnungen.
      const draft = varioArticleNumber ? { number: varioArticleNumber } : {};
      const { data, error } = await ctx.supabase.rpc('vario_enqueue_article_assign', {
        _storefront: ctx.unit.storefront,
        _id_unit: ctx.unit.idUnit,
        _vario_article_id: varioArticleId,
        _create_if_missing: false,
        _draft: draft,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Zuweisen fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.innerHTML = originalHtml;
    }
  }

  /**
   * Aufrufer ist ausschliesslich der zweistufige Klick-Handler oben — der IST die Bestaetigung.
   * Hier stand bis 2026-08-02 zusaetzlich ein `window.confirm`: Der Kunde musste zweimal klicken
   * UND danach noch einen Browser-Dialog wegdruecken. Browser-Dialoge sind projektweit verboten,
   * der Hinweis auf die Folgen steht jetzt im geschaerften Zustand direkt im Fenster.
   */
  async function unassign(unassignBtn: HTMLButtonElement): Promise<void> {
    const restoreHtml = unassignBtn.innerHTML;
    unassignBtn.disabled = true;
    unassignBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Aufheben …';
    clearError();
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_unassign_vario_listing', {
        _storefront: ctx.unit.storefront,
        _id_unit: ctx.unit.idUnit,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (!jobId) throw new Error('Kein job_id zurückgeliefert');
      const job = await awaitJob(ctx.supabase, jobId, 60_000);
      if (job.status !== 'completed') throw new Error(job.error ?? `Job ${job.status}`);
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Aufheben fehlgeschlagen: ${(e as Error).message}`);
      unassignBtn.disabled = false;
      unassignBtn.innerHTML = restoreHtml;
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
